
import { ArticleGroupedRibbons, Headlines, Data as ArticleData } from '@root/modules/article/types/article';
import articleConfig from '@root/modules/article/config/article.config';

import articleScroll from '@root/modules/article/utils/articleScrollHandler';
import getArticleRibbons from '@root/modules/article/utils/getArticleRibbons';

// Article info section
import ArticleInfoNormalTemplate from '@root/modules/article/components/info/ArticleInfoNormalTemplate.vue';
import ArticleInfoFullTemplate from '@root/modules/article/components/info/ArticleInfoFullTemplate.vue';
import ArticleInfoSmallTemplate from '@root/modules/article/components/info/ArticleInfoSmallTemplate.vue';
import ArticleInfoMultimedijaTemplate from '@root/modules/article/components/info/ArticleInfoMultimedijaTemplate.vue';
import ArticleInfoFullWidthTemplate from '@root/modules/article/components/info/ArticleInfoFullWidthTemplate.vue';
import ArticleAdultLayer from '@root/modules/article/components/ArticleAdultLayer.vue';
import ArticleRibbons from '@root/modules/article/components/ArticleRibbons.vue';

// Article body components
import ArticleBody from '@root/modules/article/components/ArticleBody.vue';
import ArticleBodyMultipage from '@root/modules/article/components/ArticleBodyMultipage.vue';
import ArticleHeadlinesSection from '@root/modules/article/components/headlines/ArticleHeadlinesSection.vue';
import ArticleInfoSocial from '@root/modules/article/components/info/ArticleInfoSocial.vue';
import AdFormBanner from '@ads/components/adForm/AdFormBanner.vue';
import getBannerKey from '@root/modules/ads/utils/getBannerKey';

import isFullWidthArticle from '@root/modules/article/utils/isFullWidthArticle';
import isMobileView from '@root/common/utils/isMobileView';

interface Data {
  isMobileView: boolean;
  relatedArticlesSettings: {
    minParagraphsLength: number;
    afterParagraphPosition: number;
    articlesLimit: number;
    articlesInBodyLimit: number;
    endOfArticleBody: boolean;
    disableRelatedArticles: boolean;
  };
  fontSize: number;
}

interface Methods {
  confirmAdult: () => void;
  handleSetFontSize: () => void;
  getKey: (params: Record<string, any>) => string;
}

interface Computed {
  isCrArticle: boolean;
  topSection: string;
  topFragmentsNumber: number;
  isAdultArticle: boolean;
  isMultipage: boolean;
  contentCenterClass: Record<string, boolean>;
  articleRibbons: ArticleGroupedRibbons | null;
  disableAds: boolean;
  disabledAdsList: string[];
  adPlacements: Record<string, any>;
  contentAdPlacement: string;
  contentFallbackAdPlacement: string;
  contentFirstAdPlacement: Record<string, any>;
  isMultimedijaArticle: boolean;
  disallowPrintingClass: Record<string, boolean>;
  isFullWidthArticle: boolean;
  isAdultConfirmed: boolean;
}

interface Props {
  article: ArticleData;
  headlines: Headlines | [];
  isArticleFetching: boolean;
}

const topSectionMapping: Record<string, string> = {
  default: 'ArticleInfoNormalTemplate',
  fullImage: 'ArticleInfoFullTemplate',
  smallImage: 'ArticleInfoSmallTemplate',
  wideImage: 'ArticleInfoNormalTemplate',
  multimedija: 'ArticleInfoMultimedijaTemplate',
  fullWidth: 'ArticleInfoFullWidthTemplate',
};

export default articleScroll.extend<Data, Methods, Computed, Props>({
  components: {
    ArticleInfoNormalTemplate,
    ArticleInfoFullTemplate,
    ArticleInfoSmallTemplate,
    ArticleInfoMultimedijaTemplate,
    ArticleInfoFullWidthTemplate,
    ArticleBody,
    ArticleBodyMultipage,
    ArticleHeadlinesSection,
    ArticleAdultLayer,
    ArticleRibbons,
    ArticleInfoSocial,
    AdFormBanner,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
    headlines: {
      type: Array,
      required: true,
    },
    isArticleFetching: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isMobileView: false,
      relatedArticlesSettings: this.$channelConfig('page').article.component.relatedArticles,
      fontSize: 1,
    };
  },
  computed: {
    isCrArticle() {
      const articles = this.$store.getters['article/getCrArticles'];

      return articles.find((article: number) => article === this.article.id);
    },
    topSection() {
      const template = this.$channelConfig('page').article.component.info.template || this.article.settings.articleTemplate;
      return topSectionMapping[template] || 'ArticleInfoNormalTemplate';
    },
    topFragmentsNumber() {
      const leadPosition = this.$channelConfig('page').article.component.info.leadPosition;
      return leadPosition === 'top' || this.topSection === 'ArticleInfoSmallTemplate' ? articleConfig.ads.first_position : 0;
    },
    isAdultArticle() {
      return this.article.settings['icon.adult'] || this.article.settings['articleAdultContent'] || false;
    },
    isMultipage() {
      return this.article.settings.isMultiPage || false;
    },
    contentCenterClass() {
      return { 'content-center': this.article.banners.disableAds };
    },
    articleRibbons() {
      const ribbonsData = this.$channelConfig('page').article?.component?.ribbonsData;
      if (!ribbonsData) {
        return null;
      }

      const articleRibbons = getArticleRibbons(this.article, ribbonsData);

      return articleRibbons;
    },
    disableAds() {
      return this.article.banners.disableAds;
    },
    disabledAdsList() {
      return this.$store.getters['adForm/getDisabledAdsList'];
    },
    adPlacements() {
      const placements: Record<string, any> = this.$store.getters['adForm/getAdsPlacements'];

      return placements || {};
    },
    contentAdPlacement() {
      return (this.adPlacements?.content?.mid?.length && this.adPlacements?.content?.mid[0]) || null;
    },
    contentFallbackAdPlacement() {
      return (this.adPlacements?.mobilecrarticle?.mid?.length && this.adPlacements?.mobilecrarticle?.mid[0]) || null;
    },
    contentFirstAdPlacement() {
      const placements: Record<string, any> = { desktop: null, mobile: null };

      if (this.adPlacements?.contentfirst?.mid?.length && this.adPlacements?.contentfirst?.mid[0]) {
        placements.desktop = this.adPlacements.contentfirst.mid[0] || null;
      }

      if (this.adPlacements?.mobilecontentfirst?.mid?.length && this.adPlacements?.mobilecontentfirst?.mid[0]) {
        placements.mobile = this.adPlacements.mobilecontentfirst.mid[0] || null;
      }

      return placements;
    },
    isMultimedijaArticle() {
      const { id } = this.$channelConfig('settings');

      return id === 'multimedija';
    },
    disallowPrintingClass() {
      return { 'display-print-none': this.article.content.paywall.enabled };
    },
    isFullWidthArticle() {
      return isFullWidthArticle(this.article);
    },
    isAdultConfirmed() {
      return this.$store.state.article.isAdultConfirmed;
    },
  },
  async mounted() {
    this.isMobileView = isMobileView();

    this.$nextTick(() => {
      this.$emit('article-loaded', this.article.id);
    });
  },
  methods: {
    confirmAdult() {
      this.$store.commit('article/setIsAdultConfirmed', true);
    },
    handleSetFontSize() {
      this.fontSize = this.fontSize === 3 ? 1 : this.fontSize + 1;
    },
    getKey({ prefix, index }) {
      return getBannerKey({ prefix, route: this.$route, store: this.$store, index });
    },
  },
});
